<template>
  <div>
    <PremiumLoader v-if="loading" @loading-complete="handleLoadingComplete" />
    <section ref="heroSection" class="hero-new">
      <video
        ref="heroVideo"
        class="hero-video"
        :class="{ 'video-loaded': videoLoaded }"
        autoplay
        loop
        muted
        playsinline
        preload="auto"
        :poster="posterImage"
        @loadedmetadata="handleVideoLoaded"
        @loadeddata="handleVideoLoaded"
        @canplay="handleVideoCanPlay"
        @canplaythrough="handleVideoCanPlay"
        @playing="handleVideoPlaying"
      >
        <source src="@/assets/videos/homevideo.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      <div
        v-if="!videoLoaded && !loading"
        class="hero-fallback"
        :style="{ backgroundImage: `url(${posterImage})` }"
      ></div>

      <div class="container-full" :class="{ 'content-visible': !loading }">
        <div class="hero-content">
          <h1 class="main-title">
            <span class="gradient-text">Meet Our Expert Team</span>
            <br />
            <span class="sliding-text" :key="currentHeadline">
              {{ currentHeadline }}
            </span>
          </h1>
          <p class="subtitle">
            Highly-skilled professionals dedicated to providing you with top-notch service.
          </p>
          <button class="scroll-button" @click="scrollToPackages">View Packages</button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import PremiumLoader from '@/components/PremiumLoader.vue';

export default {
  name: 'HeroSection',
  components: {
    PremiumLoader,
  },
  props: {
    workers: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      videoLoaded: false,
      preloadStarted: false,
      posterImage: require('@/assets/images/HomeBanner.avif'),
      isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1),
      slidingHeadlines: [
        'Your Trusted Partner for Home Services',
        'Your Trusted Partner for Quality Craftsmanship',
        'Your Trusted Partner for Reliable Solutions',
        'Your Trusted Partner for Peace of Mind',
        'Your Trusted Partner for Professional Care',
      ],
      currentHeadlineIndex: 0,
      currentHeadline: 'Your Trusted Partner for Home Services',
      textInterval: null,
    };
  },
  async mounted() {
    this.$emit('loading', true);
    this.preloadVideo();
    setTimeout(() => {
      if (this.loading) {
        this.handleLoadingComplete();
      }
    }, 3000);
    this.startHeadlineSlider();
  },
  beforeUnmount() {
    clearInterval(this.textInterval);
  },
  methods: {
    async preloadVideo() {
      if (this.preloadStarted) return;
      this.preloadStarted = true;

      try {
        const video = this.$refs.heroVideo;
        if (video) {
          video.load();
          await new Promise((resolve) => {
            const checkReady = () => {
              if (video.readyState >= 2) {
                resolve();
              } else {
                setTimeout(checkReady, 50);
              }
            };
            checkReady();
          });
          this.ensureVideoPlaying();
        }
      } catch (error) {
        console.warn('Video preload failed:', error);
      }
    },

    handleVideoLoaded() {
      if (!this.isIOS) this.videoLoaded = true;
      this.ensureVideoPlaying();
      if (this.loading && !this.isIOS) {
        requestAnimationFrame(() => {
          setTimeout(() => {
            this.handleLoadingComplete();
          }, 400);
        });
      }
    },

    handleVideoCanPlay() {
      if (!this.isIOS) this.videoLoaded = true;
      this.ensureVideoPlaying();
      if (this.loading && !this.isIOS) {
        setTimeout(() => {
          this.handleLoadingComplete();
        }, 300);
      }
    },

    handleVideoPlaying() {
      this.videoLoaded = true;
      if (this.loading) {
        setTimeout(() => {
          this.handleLoadingComplete();
        }, 200);
      }
    },

    ensureVideoPlaying() {
      const video = this.$refs.heroVideo;
      if (video && video.paused && !video.ended) {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {});
        }
      }
    },

    handleLoadingComplete() {
      this.loading = false;
      this.$emit('loading-complete');
      this.$emit('loading', false);
    },

    scrollToPackages() {
      this.$emit('scroll-to-packages');
    },

    startHeadlineSlider() {
      this.textInterval = setInterval(() => {
        this.currentHeadlineIndex = (this.currentHeadlineIndex + 1) % this.slidingHeadlines.length;
        this.currentHeadline = this.slidingHeadlines[this.currentHeadlineIndex];
      }, 3000);
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,opsz,wght@0,6..12,200..1000;1,6..12,200..1000&display=swap');

* {
  box-sizing: border-box;
}

.hero-new {
  font-family: 'Nunito Sans', sans-serif;
  min-height: 100vh;
  width: 100%;
  max-width: 100vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  position: relative;
  overflow: hidden;
  clip-path: polygon(0 0, 100% 0, 100% 85%, 0 100%);
  background: #000; /* Dark background while loading */
}

.hero-video {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  opacity: 0;
  transform: translateZ(0) scale(1.02);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  will-change: opacity;
  transition: opacity 1s cubic-bezier(0.4, 0, 0.2, 1);
}

.hero-video.video-loaded {
  opacity: 1;
  transition: opacity 1s cubic-bezier(0.4, 0, 0.2, 1);
}

.hero-fallback {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 0;
  opacity: 0.8;
}

.hero-new::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1;
}

.container-full {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
  text-align: center;
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.8s ease-out,
    transform 0.8s ease-out;
}

.container-full.content-visible {
  opacity: 1;
  transform: translateY(0);
  animation: fadeIn 1.5s ease-out;
}

.hero-content {
  margin-bottom: 0;
}

.main-title {
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 900;
  color: #fff;
  line-height: 1.2;
  margin-bottom: 1rem;
  text-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
  letter-spacing: -0.05em;
}

.gradient-text {
  background: linear-gradient(45deg, #f46523, #ff9800);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.sliding-text {
  display: inline-block;
  animation: slideHeadline 0.8s cubic-bezier(0.23, 1, 0.32, 1) forwards;
}

.subtitle {
  font-size: clamp(1rem, 2vw, 1.25rem);
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  animation: fadeIn 1.2s ease-out 0.8s both;
}

.scroll-button {
  background: linear-gradient(45deg, #f46523, #ff9800);
  border: none;
  border-radius: 50px;
  color: white;
  padding: 16px 32px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(244, 101, 35, 0.4);
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;
  margin-top: 40px;
  animation: fadeIn 1.2s ease-out 1s both;
}

.scroll-button:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(244, 101, 35, 0.5);
}

.scroll-button:active {
  transform: translateY(-2px);
}

/* --- Animations --- */
@keyframes slideHeadline {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}


@media (max-width: 960px) {
  .hero-new {
    clip-path: polygon(0 0, 100% 0, 100% 90%, 0 100%);
    padding: 60px 16px;
  }

  .hero-content {
    margin-bottom: 0;
  }

  .main-title {
    font-size: clamp(2rem, 6vw, 3rem);
  }

  .subtitle {
    font-size: clamp(0.9rem, 2.5vw, 1.1rem);
  }
}

@media (max-width: 600px) {
  .hero-new {
    clip-path: none;
    padding: 40px 12px;
  }

  .hero-video {
    position: absolute;
    transform: translateZ(0) scale(1);
  }

  .hero-new::before {
    background: rgba(0, 0, 0, 0.5);
  }

  .hero-content {
    margin-bottom: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero-video {
    animation: none;
    transition: opacity 0.3s ease;
  }

  .container-full {
    animation: none;
    transition: opacity 0.3s ease;
  }
}

body {
  margin: 0;
  padding: 0;
  overflow-x: hidden;
  width: 100%;
  max-width: 100%;
}

html {
  overflow-x: hidden;
  width: 100%;
  max-width: 100%;
}
</style>
